import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

function useCountUp(end, duration = 2000) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStarted(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    let start = 0;
    const step = end / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [started, end, duration]);

  return [count, ref];
}

function formatNumber(num) {
  if (num >= 100000) return (num / 1000).toFixed(0) + 'K';
  if (num >= 10000) return (num / 1000).toFixed(0) + 'K';
  return num.toLocaleString();
}

const stats = [
  { value: 5000, suffix: '+', label: 'Students', description: 'Active learners across India' },
  { value: 50000, suffix: '+', label: 'Questions', description: 'In our question bank' },
  { value: 5, suffix: '+', label: 'Boards', description: 'Covered comprehensively' },
  { value: 98, suffix: '%', label: 'Satisfaction', description: 'Student approval rating' },
];

function StatCard({ stat, index }) {
  const [value, ref] = useCountUp(stat.value, 2000);
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      className="glass rounded-2xl p-8 text-center hover:bg-white/[0.06] transition-all duration-300"
    >
      <div className="text-4xl md:text-5xl font-black font-display gradient-text mb-2">
        {formatNumber(value)}{stat.suffix}
      </div>
      <div className="text-white font-semibold text-lg mb-1">{stat.label}</div>
      <div className="text-gray-500 text-sm">{stat.description}</div>
    </motion.div>
  );
}

export default function Stats() {
  return (
    <section className="section-padding relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-brand-900/10 to-transparent" />

      <div className="container-custom mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold font-display text-white mb-4">
            Trusted by <span className="gradient-text">thousands</span>
          </h2>
          <p className="text-gray-400 text-lg">The numbers speak for themselves.</p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, i) => (
            <StatCard key={i} stat={stat} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
